import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, Checkbox, Field, FieldLabel, FieldError } from '../components/ui';
import { SocialButton } from '../components/ui/SocialButton';
import { PlatformCheckoutBrand } from '../components/auth/PlatformCheckoutBrand';
import { PlatformCheckoutShell } from '../components/auth/PlatformCheckoutShell';
import { useAuthStore } from '../store/auth';
import { handleSocialLoginFlow, navigateAfterSocialRegistration } from '../utils/registerHelpers';
import {
  consumeSocialAuthRedirectIfReady,
  isSocialAuthRedirectInitiatedError,
  prepareSocialAuthContext,
  readSocialAuthRedirectError,
} from '../utils/socialAuthRedirect';
import {
  parseBillingInterval,
  persistBillingInterval,
  readStoredBillingInterval,
} from '../utils/billingInterval';
import { LEGAL_VERSIONS } from '@court-sync/shared';
import { toast } from 'react-hot-toast';
import { mergeOnboardingDraft } from '../utils/onboardingDraft';
import {
  trackRegisterLanding,
  trackRegisterFormStarted,
  trackRegisterSubmit,
} from '../utils/gtmTracking';
import {
  applyPricingSelectionToSession,
  buildPricingFastTrackDraftPatch,
  parsePricingSelectionFromSearchParams,
} from '../utils/pricingSelection';

const registerSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
  termsAccepted: z.boolean().refine(val => val === true, {
    message: 'You must accept the Terms of Service',
  }),
  privacyAccepted: z.boolean().refine(val => val === true, {
    message: 'You must accept the Privacy Policy',
  }),
  marketingEmailConsent: z.boolean().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof registerSchema>;

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pricingSelection = parsePricingSelectionFromSearchParams(searchParams);
  const { register: registerUser, user, isInitialized } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);

  useEffect(() => {
    const pricingSelection = parsePricingSelectionFromSearchParams(searchParams);
    if (pricingSelection) {
      applyPricingSelectionToSession(pricingSelection);
      return;
    }

    const planId = searchParams.get('planId');
    if (planId) {
      sessionStorage.setItem('selectedPlanId', planId);
    }
    const billingInterval = parseBillingInterval(searchParams.get('billingInterval'));
    if (billingInterval) {
      persistBillingInterval(billingInterval);
    }
  }, [searchParams]);

  useEffect(() => {
    const pricingSelection = parsePricingSelectionFromSearchParams(searchParams);
    const planId =
      pricingSelection?.planId ||
      searchParams.get('planId') ||
      sessionStorage.getItem('selectedPlanId');
    const referrer = typeof document !== 'undefined' ? document.referrer : '';
    const fromPricing =
      pricingSelection?.source === 'pricing' || referrer.includes('/pricing') || !!planId;

    trackRegisterLanding({
      planId: planId || null,
      referrer: referrer || undefined,
      fromPricing,
      productLine: pricingSelection?.productLine,
    });
  }, [searchParams]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const password = watch('password', '');

  const hasTrackedFormStart = React.useRef(false);
  const handleFormFieldFocus = () => {
    if (!hasTrackedFormStart.current) {
      hasTrackedFormStart.current = true;
      trackRegisterFormStarted();
    }
  };

  useEffect(() => {
    if (!isInitialized) return;

    const redirectError = readSocialAuthRedirectError();
    if (redirectError) {
      setError('root', { message: redirectError });
    }
  }, [isInitialized, setError]);

  useEffect(() => {
    if (!isInitialized || !user) return;

    const consumed = consumeSocialAuthRedirectIfReady();
    if (!consumed?.hadRedirect) return;
    if (consumed.pending != null && consumed.pending.flow !== 'register') return;

    void navigateAfterSocialRegistration(
      navigate,
      user,
      consumed.pending?.planId ?? undefined
    );
  }, [isInitialized, user, navigate]);

  const onSubmit = async (data: RegisterFormData) => {
    const selectedPlanId = sessionStorage.getItem('selectedPlanId') || searchParams.get('planId');
    try {
      setIsLoading(true);
      const displayName = `${data.firstName} ${data.lastName}`;

      const result = await registerUser(
        data.email,
        data.password,
        displayName,
        'academy_owner',
        undefined,
        '/academy-setup',
        {
          termsAccepted: data.termsAccepted,
          privacyAccepted: data.privacyAccepted,
          termsVersion: LEGAL_VERSIONS.TERMS,
          privacyVersion: LEGAL_VERSIONS.PRIVACY,
          marketingEmailConsent: data.marketingEmailConsent || false,
        },
        undefined,
        undefined
      );

      await useAuthStore.getState().refreshUser();

      if (pricingSelection) {
        applyPricingSelectionToSession(pricingSelection);
        mergeOnboardingDraft(buildPricingFastTrackDraftPatch(pricingSelection));
      } else {
        mergeOnboardingDraft({
          currentStepId: 'sports',
          completedStepIds: [],
        });
      }

      trackRegisterSubmit(true, selectedPlanId);

      const academySetupPath = '/academy-setup';

      if (result.verificationEmailFailed) {
        toast.error(
          'Account created, but we could not send a verification email. You can verify after checkout from your dashboard.',
          { duration: 6000 }
        );
      }

      navigate(academySetupPath, {
        state: {
          email: data.email,
          message: pricingSelection
            ? 'Great! Add your organization details, then continue to checkout for your selected plan.'
            : result.needsVerification
              ? 'Great! Tell us about your organization. You can verify your email after checkout.'
              : 'Great! Tell us about your academy so we can recommend the right plan.',
        },
      });
    } catch (error: unknown) {
      trackRegisterSubmit(false, selectedPlanId);
      const message =
        (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
          ? error.message
          : null) || 'Failed to create account';
      setError('root', { message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'facebook' | 'github') => {
    try {
      setSocialLoading(provider);
      if (pricingSelection) {
        applyPricingSelectionToSession(pricingSelection);
        mergeOnboardingDraft(buildPricingFastTrackDraftPatch(pricingSelection));
      }
      const planId = searchParams.get('planId') || sessionStorage.getItem('selectedPlanId');
      if (planId) {
        sessionStorage.setItem('selectedPlanId', planId);
      }
      const billingInterval = parseBillingInterval(searchParams.get('billingInterval'));
      if (billingInterval) {
        persistBillingInterval(billingInterval);
      } else {
        const storedInterval = readStoredBillingInterval();
        if (storedInterval) {
          persistBillingInterval(storedInterval);
        }
      }
      prepareSocialAuthContext({
        flow: 'register',
        provider,
        role: 'academy_owner',
        planId,
      });
      await handleSocialLoginFlow(provider, navigate, planId || undefined);
    } catch (error: unknown) {
      if (isSocialAuthRedirectInitiatedError(error)) {
        return;
      }
      const message =
        (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
          ? error.message
          : null) || `Failed to sign in with ${provider}`;
      setError('root', { message });
    } finally {
      setSocialLoading(null);
    }
  };

  const getPasswordStrength = (pwd: string) => {
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/[a-z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[^A-Za-z0-9]/.test(pwd)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(password);
  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];

  const registerLeft = (
    <>
      <PlatformCheckoutBrand className="mb-8" />
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
        Create your account
      </h1>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        {pricingSelection
          ? 'Start your 14-day free trial. Next you’ll add your organization details, then continue to checkout for the plan you selected.'
          : 'Start your 14-day free trial. Next you’ll answer a few quick questions so we can recommend the right plan.'}
      </p>
      <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
        <li className="flex gap-2">
          <span className="text-green-600 dark:text-green-400 font-semibold shrink-0">✓</span>
          <span>Tennis, pickleball, padel, and more</span>
        </li>
        <li className="flex gap-2">
          <span className="text-green-600 dark:text-green-400 font-semibold shrink-0">✓</span>
          <span>
            {pricingSelection ? 'Checkout for the plan you selected' : 'Personalized plan recommendation'}
          </span>
        </li>
        <li className="flex gap-2">
          <span className="text-green-600 dark:text-green-400 font-semibold shrink-0">✓</span>
          <span>No charge until your trial ends</span>
        </li>
      </ul>
      <p className="mt-8 text-sm text-gray-500 dark:text-gray-500">
        Already have an account?{' '}
        <Link to="/login" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
          Sign in
        </Link>
      </p>
    </>
  );

  return (
    <PlatformCheckoutShell asideVariant="onboarding" left={registerLeft}>
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center lg:max-w-lg">
        <div>
          <div className="mb-6 space-y-1">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Account information
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Sign up with Google or your email
            </p>
          </div>

          <div className="space-y-6">
            <div className="space-y-3">
              <SocialButton
                provider="google"
                onClick={() => handleSocialLogin('google')}
                isLoading={socialLoading === 'google'}
                disabled={!!socialLoading || isLoading}
              >
                Sign up with Google
              </SocialButton>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-950 text-gray-500 dark:text-gray-400">
                  Or continue with email
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field data-invalid={!!errors.firstName?.message}>
                  <FieldLabel htmlFor="firstName">First name</FieldLabel>
                  <Input
                    id="firstName"
                    autoComplete="given-name"
                    aria-invalid={!!errors.firstName?.message}
                    onFocus={handleFormFieldFocus}
                    {...register('firstName')}
                  />
                  <FieldError>{errors.firstName?.message}</FieldError>
                </Field>
                <Field data-invalid={!!errors.lastName?.message}>
                  <FieldLabel htmlFor="lastName">Last name</FieldLabel>
                  <Input
                    id="lastName"
                    autoComplete="family-name"
                    aria-invalid={!!errors.lastName?.message}
                    {...register('lastName')}
                  />
                  <FieldError>{errors.lastName?.message}</FieldError>
                </Field>
              </div>

              <Field data-invalid={!!errors.email?.message}>
                <FieldLabel htmlFor="email">Email address</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={!!errors.email?.message}
                  {...register('email')}
                />
                <FieldError>{errors.email?.message}</FieldError>
              </Field>

              <div>
                <Field data-invalid={!!errors.password?.message}>
                  <FieldLabel htmlFor="password">Password</FieldLabel>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    aria-invalid={!!errors.password?.message}
                    {...register('password')}
                  />
                  <FieldError>{errors.password?.message}</FieldError>
                </Field>
                {password ? (
                  <div className="mt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Password strength:</span>
                      <span className={`font-medium ${passwordStrength >= 3 ? 'text-green-600' : 'text-orange-600'}`}>
                        {strengthLabels[passwordStrength - 1] || 'Very Weak'}
                      </span>
                    </div>
                    <div className="mt-1 flex space-x-1">
                      {[1, 2, 3, 4, 5].map((level) => (
                        <div
                          key={level}
                          className={`h-1 flex-1 rounded ${level <= passwordStrength ? strengthColors[passwordStrength - 1] : 'bg-gray-200'}`}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

              <Field data-invalid={!!errors.confirmPassword?.message}>
                <FieldLabel htmlFor="confirmPassword">Confirm password</FieldLabel>
                <Input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  aria-invalid={!!errors.confirmPassword?.message}
                  {...register('confirmPassword')}
                />
                <FieldError>{errors.confirmPassword?.message}</FieldError>
              </Field>

              <div className="space-y-3 pt-2">
                <Controller
                  name="termsAccepted"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-start space-x-2 cursor-pointer">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="mt-1"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        I agree to the{' '}
                        <Link to="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary-600 dark:hover:text-primary-400">
                          Terms of Service
                        </Link>
                        {errors.termsAccepted ? (
                          <span className="block text-red-600 dark:text-red-400 text-xs mt-1">
                            {errors.termsAccepted.message}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  )}
                />
                <Controller
                  name="privacyAccepted"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-start space-x-2 cursor-pointer">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="mt-1"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        I agree to the{' '}
                        <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary-600 dark:hover:text-primary-400">
                          Privacy Policy
                        </Link>
                        {errors.privacyAccepted ? (
                          <span className="block text-red-600 dark:text-red-400 text-xs mt-1">
                            {errors.privacyAccepted.message}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  )}
                />
                <Controller
                  name="marketingEmailConsent"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-start space-x-2 cursor-pointer p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="mt-1"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        <span className="font-medium">Yes, please!</span> Send me tips and updates to help my academy grow (optional).
                      </span>
                    </label>
                  )}
                />
              </div>

              {errors.root ? (
                <div className="rounded-md bg-red-50 p-4 dark:bg-red-900/20">
                  <p className="text-sm text-red-800 dark:text-red-400">{errors.root.message}</p>
                </div>
              ) : null}
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              isLoading={isLoading}
              disabled={isLoading}
            >
              {isLoading ? 'Creating account...' : 'Continue'}
            </Button>
          </form>
        </div>
      </div>
    </PlatformCheckoutShell>
  );
}
